import clsx from 'clsx';
import type { FunctionComponent, HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

import { Button } from '../button/Button';

export type CardProps = HTMLAttributes<HTMLDivElement> & {
    readonly children: ReactNode;
    readonly disabled?: boolean;
    readonly isActionPending?: boolean;
    readonly isActive?: boolean;
    readonly isCopied?: boolean;
    readonly isCut?: boolean;
    readonly isRaised?: boolean;
    readonly isSelected?: boolean;
};

export const CardRow: FunctionComponent<HTMLAttributes<HTMLDivElement> & { readonly children: ReactNode }> = ({
    children,
    className,
    ...props
}) => (
    <div
        className={clsx('flex flex-row flex-1 shrink items-center justify-center gap-s min-w-0 p-0 w-full', className)}
        {...props}
    >
        {children}
    </div>
);

export const CardText: FunctionComponent<HTMLAttributes<HTMLSpanElement> & { readonly children: ReactNode }> = ({
    children,
    className,
    ...props
}) => (
    <span
        className={clsx(
            'flex flex-1 gap-xs justify-center min-w-0 w-full',
            'text-xs font-medium leading-[1rem] text-black-700 tracking-[-0.14px]',
            'overflow-hidden text-ellipsis whitespace-nowrap select-none',
            className,
        )}
        {...props}
    >
        {children}
    </span>
);

export const CardButton: FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
    readonly isActive: boolean;
    readonly isSelected: boolean;
    readonly [key: string]: unknown;
}> = ({ children, className, isActive, isSelected, ...props }) => (
    <Button
        className={clsx(
            'text-gray-700',
            'hover:opacity-80',
            'focus-visible:opacity-80 focus-visible:outline focus-visible:outline-1 focus-visible:outline-orange-800',
            isActive && 'text-white-200 hover:bg-transparent',
            isSelected && '[&>button]:bg-transparent [&>button]:text-gray-700 [&>button]:hover:bg-transparent',
            className,
        )}
        {...props}
    >
        {children}
    </Button>
);

export const SmallRow: FunctionComponent<HTMLAttributes<HTMLDivElement> & { readonly children: ReactNode }> = ({
    children,
    className,
    ...props
}) => (
    <div className={clsx('flex items-center text-gray-600 text-xxs gap-xs pt-xs', className)} {...props}>
        {children}
    </div>
);

const getCardClassName = ({
    className,
    disabled,
    isActionPending,
    isActive,
    isCopied,
    isCut,
    isRaised,
    isSelected,
}: Pick<
    CardProps,
    'className' | 'disabled' | 'isActionPending' | 'isActive' | 'isCopied' | 'isCut' | 'isRaised' | 'isSelected'
>): string =>
    clsx(
        'flex flex-col cursor-pointer overflow-hidden px-xl py-xl font-sans',
        'rounded-xl shadow-sm',
        'transition-shadow duration-200 ease-in-out will-change-[box-shadow]',
        'focus-visible:outline focus-visible:outline-1 focus-visible:outline-orange-800',
        disabled === true && 'opacity-50',
        isRaised === true && 'shadow-lg',
        isSelected === true && isActive !== true && 'bg-white-200 text-gray-700 outline outline-1 outline-orange-700',
        isActive === true && 'bg-orange-800 text-white-200 hover:shadow-sm',
        isCopied === true && isActive === true && 'border border-dashed border-gray-600',
        isCopied === true && isActive !== true && 'bg-gray-200 text-black-700',
        isCut === true && 'border border-dotted border-orange-600 opacity-80',
        isActionPending === true && 'pending-copy-border pointer-events-none opacity-70',
        isActive !== true && isCopied !== true && isActionPending !== true && 'bg-white-200',
        className,
    );

//TODO:
// Remove the isCut, isCopied props as soon as UX is clear on how it should work
export const Card = forwardRef<HTMLDivElement, CardProps>(
    (
        { children, className, disabled, isActionPending, isActive, isCopied, isCut, isRaised, isSelected, ...props },
        ref,
    ) => {
        const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
            if (event.target === event.currentTarget && props.onClick !== undefined) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    props.onClick(event as unknown as React.MouseEvent<HTMLDivElement>);
                }
            }
        };

        return (
            // biome-ignore lint/a11y/noStaticElementInteractions: suppressed due to migration
            <div
                ref={ref}
                className={getCardClassName({
                    className,
                    disabled,
                    isActionPending,
                    isActive,
                    isCopied,
                    isCut,
                    isRaised,
                    isSelected,
                })}
                data-is-active={isActive}
                data-is-copied={isCopied}
                data-is-cut={isCut}
                data-is-selected={isSelected}
                data-testid="card-component"
                role={props.onClick !== undefined ? 'button' : undefined}
                tabIndex={isActionPending === true ? undefined : props.onClick !== undefined ? 0 : undefined}
                onKeyDown={props.onClick !== undefined ? handleKeyDown : undefined}
                {...props}
            >
                {children}
            </div>
        );
    },
);
